import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { concat, find } from 'lodash'
import Link from 'next/link'

import Button from '@/components/base/button'
import ButtonSelect, {
  ButtonSelectOption
} from '@/components/base/button-select'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import { Image } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import LoadMore from '@/components/sections/load-more'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { formatDate } from '@/utils/date'
import { getPromiseFromFetch } from '@/utils/request'
import { API, isAcademy, isCorporate } from '@/constants'

import { defaultGridRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPHeaders, WPPost, WPTaxonomy } from '@/types'

export type DefaultGridProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    collection: WPPost[]
    headers: WPHeaders
    filter_terms: WPTaxonomy[]
    years: ButtonSelectOption[]
  }>

const DefaultGrid = ({
  collection: posts,
  headers: postHeaders,
  filter_terms,
  years,
  ...props
}: DefaultGridProps) => {
  const { strings } = useStore()
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  const [currentYearFilter, setCurrentYearFilter] =
    useState<ButtonSelectOption>()
  const [currentTermFilter, setCurrentTermFilter] = useState<WPTaxonomy>()
  const [collection, setCollection] = useState<any[]>(posts || [])
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [needsPagination, setNeedsPagination] = useState(
    headers['X-WP-TotalPages'] > 1
  )

  const previousYearFilter = useRef<ButtonSelectOption>()
  const previousTermFilter = useRef<WPTaxonomy>()

  const getParams = useCallback(
    (
      page: number,
      yearFilter?: ButtonSelectOption,
      termFilter?: WPTaxonomy
    ) => {
      const perPage = 6
      const params = {
        per_page: perPage,
        page
      } as any

      if (yearFilter) {
        params['after'] = `${yearFilter?.value}-01-01T00:00:00`
        params['before'] = `${yearFilter?.value}-12-31T00:00:00`
      }

      if (termFilter) {
        params['categories'] = termFilter?.term_id
      }

      return {
        params
      }
    },
    []
  )

  const getPosts = useCallback(
    async (
      page: number,
      yearFilter?: ButtonSelectOption,
      termFilter?: WPTaxonomy
    ) => {
      const postType = 'posts'
      const needsReset =
        yearFilter?.value !== previousYearFilter.current?.value ||
        termFilter?.term_id !== previousTermFilter.current?.term_id

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/${postType}`,
        params: getParams(page, yearFilter, termFilter)
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) =>
        needsReset ? data : concat(previousCollection, data)
      )

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)
    },
    [getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination, currentYearFilter, currentTermFilter)
  }, [pagination, currentYearFilter, currentTermFilter, getPosts])

  return (
    <Motion animation={defaultGridRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <div className="flex items-center space-x-spacing-md">
              <div className="flex grow flex-col space-y-spacing-2xs md:grow-0 md:flex-row md:space-x-spacing-2xs md:space-y-0">
                <ButtonSelect
                  value={currentYearFilter?.value}
                  placeholder={strings?.labels?.placeholder_year}
                  options={years}
                  fetching={
                    fetching &&
                    currentYearFilter?.value !==
                      previousYearFilter.current?.value
                  }
                  onFilterChange={(event) => {
                    setPagination(1)
                    setCurrentYearFilter((filter) => {
                      previousYearFilter.current = filter
                      previousTermFilter.current = currentTermFilter
                      return find(years, {
                        value: event?.target.value
                      })
                    })
                  }}
                  data-motion-filter
                />

                <ButtonSelect
                  value={currentTermFilter?.term_id.toString()}
                  placeholder={strings?.labels?.placeholder_category}
                  className="min-w-[13rem]"
                  options={filter_terms?.map((filter) => ({
                    label: filter.name,
                    value: filter.term_id.toString()
                  }))}
                  fetching={
                    fetching &&
                    currentTermFilter?.term_id !==
                      previousTermFilter.current?.term_id
                  }
                  onFilterChange={(event) => {
                    setPagination(1)
                    setCurrentTermFilter((filter) => {
                      previousTermFilter.current = filter
                      previousYearFilter.current = currentYearFilter
                      return find(filter_terms, {
                        term_id: parseInt(event?.target.value)
                      })
                    })
                  }}
                  data-motion-filter
                />
              </div>

              {fetching ? (
                <Spinner variant="medium" className="hidden md:block" />
              ) : null}
            </div>

            <Spacer variant="sm" />

            <Grid className="gap-y-spacing-lg md:gap-y-spacing-2xl">
              {collection.length === 0 && strings?.labels?.no_post_found ? (
                <div className="col-span-2 md:col-span-6 lg:col-span-12">
                  <Typography variant="large" paragraph>
                    {strings.labels.no_post_found}
                  </Typography>
                </div>
              ) : (
                collection?.map((post, index) => (
                  <div
                    key={`grid-item-${index}`}
                    className={cn(
                      'col-span-2 md:col-span-3 lg:col-span-6',
                      'flex h-full flex-col space-y-spacing-sm'
                    )}
                    data-motion-post
                  >
                    {isAcademy && post.featured_image?.url ? (
                      <Link
                        href={post.link}
                        scroll={false}
                        className="relative z-10 aspect-video overflow-hidden"
                      >
                        <Image
                          className="absolute z-10 h-full w-full object-cover"
                          source={post.featured_image}
                          alt={post.featured_image?.alt}
                        />
                      </Link>
                    ) : null}
                    {post.date ? (
                      <Typography
                        variant="overline"
                        paragraph
                        className="uppercase"
                      >
                        {formatDate(post.date)}
                      </Typography>
                    ) : null}

                    <div className="flex grow flex-col space-y-spacing-lg">
                      {isCorporate && post.title?.rendered ? (
                        <Typography variant="h4" component="h3">
                          {post.title.rendered}
                        </Typography>
                      ) : null}

                      {isAcademy && post.title?.rendered ? (
                        <Link href={post.link} scroll={false}>
                          <Typography variant="h4" component="h3">
                            {post.title.rendered}
                          </Typography>
                        </Link>
                      ) : null}

                      <div className="flex flex-col space-y-spacing-sm">
                        {/* {post.subtitle ? (
                          <Typography variant="large" paragraph>
                            {post.subtitle}
                          </Typography>
                        ) : null} */}

                        {isCorporate && post.content?.rendered ? (
                          <Typography variant="regular" paragraph>
                            {post.content.rendered}
                          </Typography>
                        ) : null}

                        {isAcademy && post.excerpt?.rendered ? (
                          <Typography variant="regular" paragraph>
                            {post.excerpt.rendered}
                          </Typography>
                        ) : null}
                      </div>
                    </div>

                    {isCorporate && post.acf?.download ? (
                      <div>
                        <Button
                          isLiquid
                          asChild
                          variant="secondary"
                          className="min-h-[4.8rem] !px-spacing-xs"
                          endIcon={
                            <SVGIcon
                              symbol="pdf-icon"
                              className={cn([
                                'ml-auto size-[1.8rem] shrink-0 fill-current'
                              ])}
                            />
                          }
                        >
                          <Link
                            href={post.acf.download.url}
                            target={post.acf.download.target}
                            scroll={false}
                          >
                            <TextEffect>
                              <Typography variant="regular">
                                {post.acf.download.title}
                              </Typography>
                            </TextEffect>
                          </Link>
                        </Button>
                      </div>
                    ) : null}

                    {isAcademy && post.link ? (
                      <div>
                        <Button asChild variant="secondary">
                          <Link href={post.link} scroll={false}>
                            <TextEffect>
                              <Typography variant="regular">
                                {strings?.labels?.discover_more}
                              </Typography>
                            </TextEffect>
                          </Link>
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </Grid>

            {needsPagination ? (
              <>
                <Spacer variant="md" />

                <LoadMore
                  fetching={fetching}
                  onClick={() => {
                    setPagination((pagination) => {
                      previousYearFilter.current = currentYearFilter
                      previousTermFilter.current = currentTermFilter
                      return pagination < 0 ? 2 : pagination + 1
                    })
                  }}
                />
              </>
            ) : null}
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default DefaultGrid
